import cx from "classnames";
import * as React from "react";
import TooltipWrapper from "../../TooltipWrapper";

import { FormFieldStates, InputProps } from "./exports";

export type QESDateInputProps = InputProps & React.InputHTMLAttributes<HTMLInputElement>;

/**
 * A standard text field for easy reuse. It requires a label above, has an optional descriptive snippet below, and accepts all the same props as a native HTML input element.
 */
/* We call out the custom props here individually, so ...props is just the normal HTML input attributes */
export const QESDateInput: React.FC<React.PropsWithChildren<QESDateInputProps>> = ({ containerClassName, descriptiveText, errorText, hideLabel, isValid, label, qesRef, required, testId, wasFocusedExternally, unrequiredValidation, validationTooltipContent, ...props }) => {
  const [focused, setFocused] = React.useState(false);
  const [hasBeenFocused, setHasBeenFocused] = React.useState(false);
  const [fieldState, setFieldState] = React.useState(FormFieldStates.None);

  React.useEffect(() => {
    if (!isValid && (required || unrequiredValidation) && (hasBeenFocused || focused)) {
      setFieldState(FormFieldStates.Error);
    } else {
      setFieldState(FormFieldStates.None);
    }
  }, [isValid, hasBeenFocused, focused, required, unrequiredValidation]);

  React.useEffect(() => {
    if (wasFocusedExternally) {
      setHasBeenFocused(wasFocusedExternally);
    }

    setFocused(wasFocusedExternally);

  }, [wasFocusedExternally]);

  const subText = React.useMemo(() => {
    if (hasBeenFocused && errorText && fieldState === FormFieldStates.Error) {
      return errorText;
    } else if (descriptiveText) {
      return descriptiveText;
    }
    return "";
  }, [descriptiveText, errorText, fieldState, hasBeenFocused]);

  const handleOnBlur = () => {
    if (!hasBeenFocused) {
      setHasBeenFocused(true)
    }

    setFocused(false);
  }

  return (
    <div className={`qes-text-input__container ${containerClassName || ""}`} key={testId} onBlur={() => handleOnBlur()}>
      <label
        htmlFor={props.id || testId}
        className={cx({
          "qes-text-input__label": true,
          "visually-hidden-label": hideLabel,
        })}
      >
        {label}
        {required ? <span className="text-danger"> *</span> : ""}
        {validationTooltipContent ? <TooltipWrapper containerClassName="ml-3" text={validationTooltipContent}><i className="fa fa-question-circle" /></TooltipWrapper> : ""}
      </label>
      <div
        className={cx({
          "disabled": props.disabled,
          "error": hasBeenFocused && fieldState === FormFieldStates.Error,
          "focus": focused,
          "qes-text-input__group": true,
          [props.className]: !!props.className,
        })}
      >
        <input
          {...props}
          id={props.id || testId}
          type="date"
          className={cx({
            "error": hasBeenFocused && fieldState === FormFieldStates.Error,
            "qes-text-input__input": true,
          })}
          // input type="date" doesn't use the pattern attribute, but any browsers that don't support type="date" fall back to type="text" and this helps keep us sane
          // that's only applicable to safari and IE at this point, though
          pattern="\d{4}-\d{2}-\d{2}"
          onFocus={() => setFocused(true)}
          ref={qesRef as React.RefObject<HTMLInputElement>}
          data-testid={testId}
          required={required}
        />
      </div>
      {subText && <small className="qes-text-input__descriptive-text">{subText}</small>}
    </div>
  );
};
